/** Settings navigation shows one category at a time without changing editor ownership or routes. */
import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Pressable, ScrollView, StyleSheet, Text, View} from 'react-native';
import {Icon} from '../Icon';
import {useSharedPalette} from '../theme';

export interface SettingsNavigationEntry {
  id: string;
  label: string;
  kind?: 'heading' | 'item';
  iconPath?: string;
  iconViewBox?: string;
  available?: boolean;
}
export interface SettingsNavigationGroup {
  id: string;
  label: string;
  entries: readonly SettingsNavigationEntry[];
}
export function SettingsNavigationGroups({groups, activeId, onSelect, onOpenGroup, onBackActionChange, onReturnToSettings, resetKey, footerEntries = [], topInset = 0, bottomInset = 0, scrollTestID}: {
  groups: readonly SettingsNavigationGroup[];
  activeId: string;
  onSelect: (id: string) => void;
  onOpenGroup?: (targetId: string) => boolean | void | Promise<boolean | void>;
  onBackActionChange?: (action: (() => void) | null) => void;
  onReturnToSettings?: () => boolean | void | Promise<boolean | void>;
  resetKey?: string | number | boolean;
  footerEntries?: readonly SettingsNavigationEntry[];
  topInset?: number;
  bottomInset?: number;
  scrollTestID?: string;
}) {
  const palette = useSharedPalette();
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  useEffect(() => {setSelectedGroup(null);}, [resetKey]);
  const visibleGroups = groups.filter(group => group.entries.some(entry => entry.kind !== 'heading'));
  const selected = visibleGroups.find(item => item.id === selectedGroup);
  const group = selected && selected.entries.filter(entry => entry.kind !== 'heading').length > 1 ? selected : undefined;
  const groupId = selected?.id;
  const returnToSettings = useRef(onReturnToSettings);
  useLayoutEffect(() => {returnToSettings.current = onReturnToSettings;}, [onReturnToSettings]);
  const resetGroup = useCallback(() => {
    const approval = returnToSettings.current?.();
    if (approval instanceof Promise) void approval.then(accepted => {if (accepted !== false) setSelectedGroup(null);});
    else if (approval !== false) setSelectedGroup(null);
  }, []);
  useEffect(() => {
    onBackActionChange?.(groupId ? resetGroup : null);
    return () => onBackActionChange?.(null);
  }, [groupId, resetGroup, onBackActionChange]);
  const itemRow = (item: SettingsNavigationEntry) => {
    const disabled = item.available === false;
    return <Pressable key={item.id} accessibilityRole="button" accessibilityLabel={item.label} aria-selected={item.id === activeId} aria-disabled={disabled} accessibilityState={{selected: item.id === activeId, disabled}} disabled={disabled} onPress={() => onSelect(item.id)}>
      {({pressed}) => <View style={[styles.row, item.id === activeId && {backgroundColor:palette.track}, {opacity:disabled ? 0.4 : pressed ? 0.6 : 1}]}>
        {item.iconPath ? <Icon d={item.iconPath} viewBox={item.iconViewBox} filled={item.id === activeId} size={20} color={palette.foreground}/> : null}
        <Text style={[styles.label, {color:palette.foreground, fontWeight:item.id === activeId ? '600' : '400'}]}>{item.label}</Text>
      </View>}
    </Pressable>;
  };
  return <ScrollView testID={scrollTestID} contentContainerStyle={[styles.content, {paddingTop:topInset + 8, paddingBottom:bottomInset + 16}]}>
    {group ? <>
      {group.entries.filter(item => item.kind !== 'heading').map(itemRow)}
    </> : <>
      {visibleGroups.map(item => <Pressable key={item.id} accessibilityRole="button" accessibilityLabel={item.label} onPress={() => {
        const entries = item.entries.filter(entry => entry.kind !== 'heading' && entry.available !== false);
        const target = entries.find(entry => entry.id === activeId) ?? entries[0];
        if (!target) return;
        const approval = onOpenGroup?.(target.id);
        if (approval instanceof Promise) void approval.then(accepted => {if (accepted !== false) setSelectedGroup(item.id);});
        else if (approval !== false) setSelectedGroup(item.id);
      }}>
        {({pressed}) => <View style={[styles.row,{opacity:pressed ? 0.6 : 1}]}>
          <Text style={[styles.label,{color:palette.foreground,fontWeight:item.entries.some(entry => entry.id === activeId) ? '600' : '400'}]}>{item.label}</Text>
        </View>}
      </Pressable>)}
    </>}
    {footerEntries.map(itemRow)}
  </ScrollView>;
}
const styles = StyleSheet.create({
  content:{paddingHorizontal:8},
  row:{minHeight:44,flexDirection:'row',alignItems:'center',gap:10,paddingHorizontal:10,paddingVertical:10,borderRadius:8},
  label:{flex:1,fontSize:14},
});
